import { Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import PropTypes from 'prop-types';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ANALYTICS_BODY_FONT_FAMILY, ANALYTICS_TITLE_FONT_FAMILY } from '../chartTypography';
import {
  ANALYTICS_DIVIDER,
  ANALYTICS_TEXT_MUTED,
  ANALYTICS_TEXT_PRIMARY,
  BROADSIGHT_GREEN_PALETTE,
} from '../analyticsColors';

const useStyles = makeStyles(() => ({
  rootContainer: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'center',
    minHeight: 0,
    boxSizing: 'border-box',
  },
  metricsContainer: {
    flex: '1 1 auto',
    minHeight: 0,
    marginTop: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: '2px',
    boxSizing: 'border-box',
  },
  outerContainer: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 0,
    gap: '28px',
  },
  statBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    minWidth: 0,
    flex: '1 1 0',
    minHeight: '120px',
  },
  statLabel: {
    margin: 0,
    fontFamily: ANALYTICS_BODY_FONT_FAMILY,
    fontSize: '13px',
    lineHeight: 1.3,
    fontWeight: 400,
    color: ANALYTICS_TEXT_MUTED,
    whiteSpace: 'nowrap',
  },
  bigNumber: {
    margin: 0,
    fontFamily: ANALYTICS_TITLE_FONT_FAMILY,
    fontSize: '44px',
    lineHeight: 1.1,
    fontWeight: 700,
    color: ANALYTICS_TEXT_PRIMARY,
    letterSpacing: '-0.01em',
  },
  deltaContainer: {
    display: 'inline-flex',
    alignItems: 'baseline',
    justifyContent: 'center',
    gap: '2px',
  },
  deltaValue: {
    margin: 0,
    fontFamily: ANALYTICS_TITLE_FONT_FAMILY,
    fontSize: '44px',
    lineHeight: 1.1,
    fontWeight: 700,
    letterSpacing: '-0.01em',
  },
  deltaPositive: {
    color: BROADSIGHT_GREEN_PALETTE[1],
  },
  deltaNegative: {
    color: ANALYTICS_TEXT_MUTED,
  },
  deltaNeutral: {
    color: ANALYTICS_TEXT_MUTED,
  },
  deltaArrow: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 0,
    transform: 'translateY(-1px)',
    '& .MuiSvgIcon-root': {
      fontSize: '18px',
    },
  },
  divider: {
    border: 'none',
    borderLeft: `1px solid ${ANALYTICS_DIVIDER}`,
    width: '1px',
    height: '120px',
    alignSelf: 'center',
    margin: '0',
  },
  '@media (max-width: 520px)': {
    outerContainer: {
      flexDirection: 'column',
      alignItems: 'stretch',
      justifyContent: 'center',
      gap: '16px',
    },
    divider: {
      width: '100%',
      height: '1px',
      borderLeft: 'none',
      borderTop: `1px solid ${ANALYTICS_DIVIDER}`,
    },
    statLabel: {
      whiteSpace: 'normal',
    },
    bigNumber: {
      fontSize: '40px',
    },
    deltaValue: {
      fontSize: '40px',
    },
  },
}));

//This functional component accepts the prop -data- with the following schema:
// {
//thisMonth: Number   ;the count for this month (# of issues, # of services, etc.)
//historical: Number ;the average from past time periods of the same range.
//title: String  ;this is the title that goes in the top of the NumberBox
//subTitle: ;this is the subtitle that goes below this month's count.
// }

function NumberBox({ data }) {
  const classes = useStyles();
  const { thisMonth, historical, title, showTitle = true } = data;
  const percentChange = Math.round((thisMonth / (historical ? historical : 1)) * 100 - 100);
  const deltaPrefix = percentChange > 0 ? '+' : '';
  const isPositive = percentChange > 0;
  const isNegative = percentChange < 0;
  let deltaClassName = classes.deltaNeutral;
  if (isPositive) {
    deltaClassName = classes.deltaPositive;
  } else if (isNegative) {
    deltaClassName = classes.deltaNegative;
  }
  let deltaArrow = null;
  if (isPositive) {
    deltaArrow = (
      <span className={`${classes.deltaArrow} ${deltaClassName}`}>
        <ExpandLessIcon />
      </span>
    );
  } else if (isNegative) {
    deltaArrow = (
      <span className={`${classes.deltaArrow} ${deltaClassName}`}>
        <ExpandMoreIcon />
      </span>
    );
  }

  return (
    <div className={classes.rootContainer}>
      {showTitle && <Typography style={{ fontSize: '1.2rem' }}>{title}</Typography>}
      <div className={classes.metricsContainer}>
        <div className={classes.outerContainer}>
          <div className={classes.statBlock}>
            <Typography component="p" className={classes.statLabel}>
              Interactions in period
            </Typography>
            <Typography component="h2" className={classes.bigNumber}>
              {thisMonth}
            </Typography>
          </div>

          <hr className={classes.divider}></hr>
          <div className={classes.statBlock}>
            <Typography component="p" className={classes.statLabel}>
              Change vs previous years
            </Typography>
            <div className={classes.deltaContainer}>
              <Typography component="h2" className={`${classes.deltaValue} ${deltaClassName}`}>
                {`${deltaPrefix}${percentChange}%`}
              </Typography>
              {deltaArrow}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

NumberBox.propTypes = {
  data: PropTypes.shape({
    thisMonth: PropTypes.number,
    historical: PropTypes.number,
    title: PropTypes.string,
    showTitle: PropTypes.bool,
  }).isRequired,
};

export default NumberBox;
